import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import DataRoomContext from '../../providers/DataRoom/DataRoom.context';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { useTranslation } from 'react-i18next';
import HelmetWrapper from 'components/HelmetWrapper';
import classNames from 'classnames';
import Subnav from 'components/Subnav';
import HomeIcon from 'icons/Home.icon';
import ReportsIcon from 'icons/Reports.icon';
import { Button } from 'ncoded-component-library';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import { downloadUrlInNewWindow, getDataRoomRoute } from 'utils';
import useQueryParams from 'hooks/useQueryParams';
import useCallbackRef from 'hooks/useCallbackRef';
import dataRoomApi from '../../dataRoomApi';
import DownloadStatusContext from 'providers/DownloadStatus/DownloadStatus.context';
import downloadService from 'services/downloadService';
import DownloadIcon from 'icons/Download.icon';
import FileIcon from 'icons/File.icon';
import OnboardingContext from '../../providers/Onboarding/Onboarding.context';
import ExportAnalyticsButton from '../../components/ExportAnalyticsButton';
import ExportActivityModal from './components/ExportActivityModal';
import {
  ACTIVITY_LOG_EXPORT_FORMAT,
  ActivityLogExportFormat,
} from './pages/Reports/types';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import { getMixpanelErrorProps } from 'axiosUtils';
import { ErrorViewedProps } from 'models/MixpanelEvents';

import './Reports.styles.scss';

const ReportsRouter: React.FC = () => {
  const [isDownloading, setIsDownloading] = useState(false);

  const [exportActivityModal, exportActivityModalRef] =
    useCallbackRef<ModalRef>();

  const {
    doesntHaveAccess,
    hasIndexGenerated,
    isAdmin,
    dataRoom: { _id, name },
  } = useContext(DataRoomContext);
  const { contentCard } = useContext(OnboardingContext);
  const { popServerError } = useContext(PopNotificationsContext);
  const { startReportDownload, failReportDownload } = useContext(
    DownloadStatusContext,
  );

  const {
    params: { action, dataRoomMemberId, from, to },
  } = useQueryParams();

  const { t } = useTranslation();

  const getMixpanelProps = useGetMixpanelProps();

  const isOnOverview = useLocation().pathname.includes('overview');

  const classes = classNames('reports', 'page');

  const links = useMemo(
    () => [
      {
        to: 'overview',
        label: t('overview'),
        Icon: HomeIcon,
      },
      {
        to: 'activity-log',
        label: t('activityLog'),
        Icon: ReportsIcon,
      },
    ],
    [t],
  );

  const params = useMemo(
    () => ({
      action,
      dataRoomMemberId,
      createdAt: from || to ? { $gte: from, $lte: to } : null,
    }),
    [action, dataRoomMemberId, from, to],
  );

  const downloadIndexFile = useCallback(async () => {
    try {
      setIsDownloading(true);

      const {
        data: { url, type },
      } = await dataRoomApi.room.getExportedIndex(_id);

      if (url) downloadUrlInNewWindow(url, type);
    } catch (e) {
      popServerError(e);
    } finally {
      setIsDownloading(false);
    }
  }, [_id, popServerError]);

  const downloadPDF = useCallback(async () => {
    try {
      startReportDownload(true);

      await dataRoomApi.reports.getPdfReport(_id, {
        ...params,
        uniqueId: downloadService.downloadUniqueId,
      });
    } catch (e) {
      mixpanelService.errorViewed(
        getMixpanelProps(getMixpanelErrorProps(e)) as ErrorViewedProps,
      );
      failReportDownload();
    }
  }, [_id, params, failReportDownload, startReportDownload, getMixpanelProps]);

  const downloadCSV = useCallback(async () => {
    try {
      startReportDownload();

      await dataRoomApi.reports.getCsvReport(_id, {
        ...params,
        uniqueId: downloadService.downloadUniqueId,
      });
    } catch (e) {
      mixpanelService.errorViewed(
        getMixpanelProps(getMixpanelErrorProps(e)) as ErrorViewedProps,
      );
      failReportDownload();
    }
  }, [_id, params, failReportDownload, startReportDownload, getMixpanelProps]);

  const exportActivityLog = useCallback(
    (format: ActivityLogExportFormat) =>
      format === ACTIVITY_LOG_EXPORT_FORMAT.PDF ? downloadPDF() : downloadCSV(),
    [downloadPDF, downloadCSV],
  );

  const openExportActivityModal = useCallback(
    () => exportActivityModal?.open(),
    [exportActivityModal],
  );

  const RenderExportIndex = useMemo(
    () =>
      isOnOverview ? (
        <>
          {hasIndexGenerated && (
            <Button
              icon={FileIcon}
              onClick={downloadIndexFile}
              disabled={isDownloading}
            >
              {t('fileIndex')}
            </Button>
          )}

          <ExportAnalyticsButton label={t('export')} />
        </>
      ) : (
        <>
          <Button icon={DownloadIcon} onClick={openExportActivityModal}>
            {t('export')}
          </Button>

          <ExportActivityModal
            ref={exportActivityModalRef}
            onExport={exportActivityLog}
          />
        </>
      ),
    [
      hasIndexGenerated,
      isDownloading,
      isOnOverview,
      exportActivityLog,
      exportActivityModalRef,
      openExportActivityModal,
      downloadIndexFile,
      t,
    ],
  );

  if (!isAdmin)
    return <Navigate to={getDataRoomRoute(_id, 'documents')} replace />;

  return (
    <div className={classes}>
      <HelmetWrapper title={`${t('analytics&Insights')} - ${name}`} />
      <h2>{t('analytics&Insights')}</h2>
      {contentCard}
      <div className="reports__heading">
        <Subnav links={links} />
        {!doesntHaveAccess && <div>{RenderExportIndex}</div>}
      </div>
      <Outlet />
    </div>
  );
};

export default ReportsRouter;
